import { memo } from 'react';

import { pluralize } from 'utils';
import { TestId } from '@/types/testId';
import { cn } from '@/shadcn/lib/utils';
import { AvatarComponent } from '@/components/ui/avatar/avatar';
import { LoadingProps } from '@/types/loadingProps';
import { Skeleton } from '@/shadcn/skeleton';
import { Checkbox } from '@/shadcn/checkbox';

type Props = LoadingProps<{
  accountId: number;
  name: string;
  avatar: string | null | undefined;
  nbParticipants: number;
  isSelected: boolean;
  toggleTeam: (accountId: number) => void;
}>;
// A single clickable team row
export const TeamItem = memo((props: Props) => {
  if (props.loading) return <TeamItemSkeleton />;

  const toggleThisTeam = () => {
    props.toggleTeam(props.accountId);
  };

  return (
    <label
      key={props.accountId}
      className={cn(
        'hover:bg-accent bg-card flex cursor-pointer flex-row flex-nowrap items-center justify-between rounded-md border border-transparent px-2 py-1.5 transition-[border-color]',
        props.isSelected && 'bg-accent border-primary'
      )}
      data-cy={TestId.TeamItem.ToggleTeamName}
      htmlFor={`team-item-${props.accountId}`}
    >
      <div className="flex grow flex-row flex-nowrap items-center justify-start gap-3">
        <AvatarComponent source={props.avatar} name={props.name} />

        <div className="flex flex-col flex-nowrap items-start overflow-hidden text-sm capitalize">
          {props.name}
          <div className="text-muted-foreground text-xs">
            {pluralize(props.nbParticipants, 'participant')}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 flex-row flex-nowrap items-center">
        <Checkbox
          id={`team-item-${props.accountId}`}
          checked={props.isSelected}
          onCheckedChange={toggleThisTeam}
          aria-label={`Add the ${props.name} team to the group.`}
          data-testid={TestId.TeamItem.ToggleTeamCheckbox}
        />
      </div>
    </label>
  );
});

const TeamItemSkeleton = () => {
  return (
    <div className="flex flex-row flex-nowrap items-center justify-between rounded-md border border-transparent px-2 py-2">
      <div className="flex grow flex-row flex-nowrap items-center justify-start gap-3">
        <Skeleton className="h-8 w-8 rounded-full" />

        <div className="flex flex-col flex-nowrap items-start gap-1 overflow-hidden text-sm capitalize">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-3 w-16" />
        </div>
      </div>
      <div className="flex shrink-0 flex-row flex-nowrap items-center">
        <Skeleton className="size-4 rounded-md" />
      </div>
    </div>
  );
};
